import { useTheme, Box, Typography } from '@mui/material';
import { useState } from 'react';
import moment from 'moment';
import Feedback from '../../components/special/Feedback';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import PreviewPositions from '../../components/preview/PreviewPositions';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import SpeedDialMenu from '../../components/menu/SpeedDialMenu';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import TextCard from '../../components/cards/TextCard';
import CardGrid from '../../components/cards/layout/CardGrid';
import FileCard from '../../components/cards/FilesCard';
import DownloadIcon from '@mui/icons-material/Download';
import CardContainer from '../../components/cards/layout/CardContainer';
import PopUpContainer from '../../components/popup/PopUpContainer';
import Share from '../../components/special/Share';
import RenderHTML from '../../components/preview/RenderHTML';
import StyledTextContainer from '../../components/preview/StyledTextContainer';
import SkeletonCard from '../../components/cards/SkeletonCard';
import { useTranslation } from 'react-i18next';
import ImagesCard from '../../components/cards/ImagesCard';
import ExoResendMail from '../../components/exo/ExoResendMail';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoCard from '../../components/cards/InfoCard';
import { InfoTypes } from './invoiceTypes';
import useFetch from '../../api/useFetch';
import NewInvoice from '../../components/new/NewInvoice';
import PictureNameCard from '../../components/cards/PictureNameCards/PictureNameCard';
import ProjectCard from '../../components/cards/ProjectCard';
import MailLogCard from '../../components/cards/mailLogCard/MailLogCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  finalInvoiceCode,
  progressBill,
  progressBillCodes,
} from '../../components/new/NewProgressBilling';
import { hasPermissions } from '../../auth/hasPermissions';
import TimeBillingCard from '../../components/cards/TimeBillingCard';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoDocumentFromDraft from '../../components/exo/ExoDocumentFromDraft';
import { EditableDocumentStatus } from '../../components/special/EditableDocumentStatus';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { useAiPageContext } from '../../components/ai-chat';
import { ExoAiOriginChip } from '../../components/exo/ExoAiOriginChip';

const ExtendInvoice = ({ row, onUpdate, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const xmlTypeInfo = {
    ubl: t('XML_TYPE_INFO_UBL'),
    cii: t('XML_TYPE_INFO_CII'),
    zugferd: t('XML_TYPE_INFO_ZUGFERD'),
  };
  const config = useConfig();

  const { apiData, setApiData } = useFetch(`invoices/${row.id}`);

  useAiPageContext(
    apiData?.data && {
      type: 'invoice',
      id: apiData.data.id,
      label: [apiData.data.nr, apiData.data.title].filter(Boolean).join(' · '),
      projectId: apiData.data.project?.id,
      projectLabel: apiData.data.project?.name,
    },
  );

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const [startData, setStartData] = useState(null);
  const [open, setOpen] = useState(false);

  // cancellation invoice (Stornorechnung): opens the invoice form prefilled with
  // the negated positions of this invoice and the cancellation templates
  const CREDIT_NOTE_TYPE_CODE = 381;
  const CORRECTED_INVOICE_TYPE_CODE = 384;
  const canCancel =
    apiData?.data &&
    !apiData.data.isDraft &&
    apiData.data.status !== 5 &&
    ![CREDIT_NOTE_TYPE_CODE, CORRECTED_INVOICE_TYPE_CODE].includes(
      apiData.data.typeCode,
    );

  function handleCreateCancellation() {
    const invoice = apiData.data;
    const positions = (invoice.positions || []).map((position) => {
      const { id, timeTrackingIds, ...rest } = position;
      return { ...rest, amount: -(rest.amount || 0) };
    });

    setStartData({
      cancelledInvoiceId: invoice.id,
      title: `${t('Cancellation')}: ${invoice.title}`,
      note: invoice.note,
      introduction: invoice.introduction,
      internalNote: invoice.internalNote,
      positions,
      recipient: invoice.recipient,
      recipientType: invoice.recipientType,
      recipientAddition: invoice.recipientAddition,
      project: invoice.project,
      projectId: invoice.project?.id ?? invoice.projectId,
      paymentMethod: invoice.paymentMethod,
      isFlat: invoice.isFlat,
    });
    setOpen(true);
  }

  // invoice is editable until "editableUntil" (null = no limit)
  const editableUntil = apiData?.data?.editableUntil
    ? moment(apiData.data.editableUntil)
    : null;
  const isEditable =
    !editableUntil || moment().isSameOrBefore(editableUntil, 'day');

  // menu
  const actions = [
    {
      icon: <ModeEditIcon />,
      name: t('Edit'),
      onClick: handleEdit,
      disabled: !hasPermissions('admin') && !isEditable,
      tooltipTitle: !isEditable
        ? t('Invoice no longer editable', {
            date: editableUntil.format('DD.MM.YYYY'),
          })
        : t('Edit'),
    },
    {
      icon: <NoteAddIcon />,
      name: t('Finalize Invoice'),
      onClick: () => setOpenCreateInvoice(true),
      hidden: apiData && apiData.data.isDraft ? false : true, // only show if it's a draft
    },
    {
      icon: <ContentCopyIcon />,
      name: t('Copy'),
      onClick: handleCopy,
    },
    {
      icon: <CancelPresentationIcon />,
      name: t('Create Cancellation Invoice'),
      onClick: handleCreateCancellation,
      hidden: !canCancel,
    },
    {
      icon: <ForwardToInboxIcon />,
      name: t('Send Again'),
      onClick: () => setOpenResendMail(true),
    },
    {
      icon: <PrintIcon />,
      name: t('Print'),
      onClick: () => handlePrint(apiData && apiData.data.file.temporaryUrl),
      hidden: apiData && apiData.data.file ? false : true,
    },
    {
      icon: <ShareIcon />,
      name: t('Share'),
      onClick: () => setOpenShare(true),
    },
    {
      icon: <DeleteOutlineIcon />,
      name: t('Delete'),
      onClick: handleDelete,
      hidden: !apiData?.data?.isDraft,
    },
  ];

  function handleDelete() {
    DBRequest({
      config,
      path: `invoices/${apiData.data.id}`,
      method: API_DELETE(),
      onFeedback: () => onDelete(row),
    });
  }

  function handleEdit() {
    setStartData(apiData.data);
    setOpen(true);
  }
  function handleCopy() {
    const dataCopied = {};
    apiData.data.positions.forEach((position) => {
      delete position.id;
    });
    dataCopied.positions = apiData.data.positions;
    dataCopied.note = apiData.data.note;
    dataCopied.title = apiData.data.title;

    setStartData(dataCopied);
    setOpen(true);
  }

  function handleUpdate(data) {
    setApiData(data);
    onUpdate(data.data);
  }

  // share
  const [openShare, setOpenShare] = useState(false);
  function handleCloseShare() {
    setOpenShare(!openShare);
  }

  // print
  function handlePrint(pdfUrl) {
    // Open a new window or iframe with the PDF URL
    const printWindow = window.open(pdfUrl, '_blank');

    // Wait for the PDF to load
    printWindow.onload = () => {
      // Trigger the print dialog
      printWindow.print();
    };
  }

  //rensend mail
  const [openResendMail, setOpenResendMail] = useState(false);

  // Create Invoice from a Draft
  const [openCreateInvoice, setOpenCreateInvoice] = useState(false);

  const invoiceType = progressBillCodes.includes(startData?.typeCode)
    ? 'progressBilling'
    : 'custom';

  return (
    <Box className="flex flex-col gap-4 relative">
      {!apiData ? (
        <InvoiceSkeleton />
      ) : (
        <>
          <ExoDocumentFromDraft
            dataCollection={apiData.data}
            recipient={apiData.data.recipient}
            type="invoices"
            id={apiData.data.id}
            open={openCreateInvoice}
            onClose={() => setOpenCreateInvoice(false)}
            handleUpdate={handleUpdate}
            askConfirmation
            previewDocument={'invoice'}
          />
          <ExoResendMail
            recipient={apiData.data.recipient}
            type="invoices"
            id={apiData.data.id}
            open={openResendMail}
            onClose={() => setOpenResendMail(false)}
          />
          <SpeedDialMenu actions={actions} />
          <TextCard
            text={
              <>
                <ExoAiOriginChip record={apiData.data} />
                {apiData.data.title}
              </>
            }
          />
          <CardGrid>
            <PictureNameCard
              data={apiData.data.recipient}
              title={t('Recipient')}
              pictureUrl={
                apiData.data.recipient.logo &&
                apiData.data.recipient.logo.thumbnailUrl
              }
              name={
                apiData.data.recipient.firstName
                  ? `${apiData.data.recipient.firstName} ${apiData.data.recipient.lastName}`
                  : apiData.data.recipient.name
              }
              type={apiData.data.recipientType}
            />
            <ProjectCard project={apiData.data.project} />
            <InfosAndStatus
              invoice={apiData.data}
              row={row}
              onUpdate={onUpdate}
              setInvoice={onUpdate}
              handleAlertOpen={handleAlertOpen}
            />

            <FileCard
              files={[
                {
                  label: t('Invoice'),
                  icon: <DownloadIcon />,
                  url: apiData.data.file ? apiData.data.file.temporaryUrl : '',
                },
                {
                  label: 'X-' + t('Invoice'),
                  icon: <DownloadIcon />,
                  url: apiData.data.xrechnung
                    ? apiData.data.xrechnung.temporaryUrl
                    : '',
                  info: xmlTypeInfo[apiData.data.xmlType],
                },
              ]}
            />
            <ImagesCard images={apiData.data.additionalFiles} />

            <MailLogCard span={2} logs={apiData.data.mailLogs} />

            {apiData.data.timeTrackingData && (
              <TimeBillingCard
                timeTrackingData={apiData.data.timeTrackingData}
                timeTrackings={apiData.data.timeTrackings || []}
                span={2}
              />
            )}

            <CardContainer
              span={88}
              sx={{
                display: 'block',
                padding: '0px',
              }}
            >
              <PreviewPositions positions={apiData.data.positions} />
            </CardContainer>

            {apiData.data.note && (
              <CardContainer span={88}>
                <StyledTextContainer
                  backgroundColor="transparent"
                  fullWidth
                  color={colors.text}
                >
                  <RenderHTML HTML={apiData.data.note} />
                </StyledTextContainer>
              </CardContainer>
            )}
            {apiData.data.internalNote && (
              <CardContainer span={99}>
                <StyledTextContainer backgroundColor="transparent" fullWidth>
                  <RenderHTML HTML={apiData.data.internalNote} />
                </StyledTextContainer>
              </CardContainer>
            )}
          </CardGrid>
        </>
      )}
      <NewInvoice
        open={open}
        onUpdate={handleUpdate}
        onClose={() => setOpen(false)}
        startData={startData}
        type={invoiceType}
      />
      <PopUpContainer open={openShare} onClose={handleCloseShare}>
        <Share
          title={`${t('Share Invoice')} ${apiData && apiData.data.nr}`}
          url={
            apiData && apiData.data.file ? apiData.data.file.temporaryUrl : ''
          }
        />
      </PopUpContainer>
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

const InfosAndStatus = ({
  invoice,
  onUpdate,
  setInvoice,
  handleAlertOpen,
  row,
}) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState(invoice.status);
  const config = useConfig();

  function handleChange(newStatus) {
    setStatus(newStatus);
    if (newStatus !== status) updateStatusInDB(newStatus);
  }

  function updateStatusInDB(newStatus) {
    const request = { status: newStatus };

    DBRequest({
      config,
      path: `invoices/${invoice.id}/status`,
      method: API_PATCH(request),
      onResponse: () => {
        setInvoice({ ...invoice, status: newStatus });
        onUpdate({ ...row, status: newStatus });
      },
      onError: () => setStatus(row.status),
      onFeedback: handleAlertOpen,
    });
  }

  const customContent = (
    <Box className="flex flex-row gap-2 items-center">
      <Typography variant="h6">{t('Status')}</Typography>
      <EditableDocumentStatus
        type="invoice"
        status={status}
        onChange={handleChange}
      />
    </Box>
  );

  const infos = InfoTypes(invoice);

  return <InfoCard infos={infos} customContent={customContent} />;
};

const InvoiceSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={54} span={4} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={156} span={4} />
    </CardGrid>
  );
};

export default ExtendInvoice;
